/* GENERATED from microlab (mobile) src/modules/anatomyRecall — keep in sync; edit the mobile .ts source and regenerate. */
/*
  RecallController — Trace / Recall / Master session logic.

  Pure state + reducer; no rendering, no WebView. The practice screen
  dispatches actions and mirrors the resulting state onto the existing
  3D viewer through `webviewRecall.ts`.
*/
import { useCallback, useMemo, useReducer } from 'react';
import { masterPriority } from './progressService';
/** Camera yaw offsets used to vary the view in Master. */
const MASTER_ANGLES = [0, 40, -40, 75, -75, 160];
function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
export function buildQueue(card, stage, store) {
  if (stage === 'trace') return [...card.items];
  if (stage === 'recall') return shuffle(card.items);
  // Master: weakest first, random among equals.
  const now = Date.now();
  return shuffle(card.items).sort((a, b) => (store ? masterPriority(card.id, b.key, store, now) : 0) - (store ? masterPriority(card.id, a.key, store, now) : 0));
}
function initial(stage, queue) {
  return {
    stage,
    queue,
    index: 0,
    completed: [],
    typed: '',
    progress: 0,
    wrongAttempts: 0,
    hintLevel: 0,
    revealed: false,
    traceSlips: 0,
    feedback: {
      kind: 'none'
    },
    feedbackId: 0,
    results: [],
    finished: queue.length === 0,
    angle: 0
  };
}
function angleFor(stage, index) {
  if (stage !== 'master' || index === 0) return 0;
  return MASTER_ANGLES[Math.floor(Math.random() * MASTER_ANGLES.length)];
}
function reducer(state, action) {
  switch (action.type) {
    case 'start':
      return initial(action.stage, action.queue);
    case 'type':
      return {
        ...state,
        typed: action.typed,
        progress: action.progress
      };
    case 'traceSlip':
      return {
        ...state,
        traceSlips: state.traceSlips + 1,
        feedback: {
          kind: 'traceSlip'
        },
        feedbackId: state.feedbackId + 1
      };
    case 'hint':
      return {
        ...state,
        hintLevel: Math.min(2, state.hintLevel + 1)
      };
    case 'reveal':
      return {
        ...state,
        revealed: true,
        typed: '',
        progress: 0
      };
    case 'wrong':
      {
        const tries = state.wrongAttempts + 1;
        return {
          ...state,
          wrongAttempts: tries,
          feedback: action.close ? {
            kind: 'close',
            tries
          } : {
            kind: 'wrong',
            tries
          },
          feedbackId: state.feedbackId + 1
        };
      }
    case 'correct':
      {
        const current = state.queue[state.index];
        if (!current) return state;
        const result = {
          key: current.key,
          name: current.name,
          firstTry: state.stage === 'trace' ? state.traceSlips === 0 : state.wrongAttempts === 0 && state.hintLevel === 0 && !state.revealed,
          attempts: state.wrongAttempts + 1,
          usedHint: state.hintLevel > 0,
          revealed: state.revealed
        };
        const nextIndex = state.index + 1;
        return {
          ...state,
          index: nextIndex,
          completed: [...state.completed, current.key],
          typed: '',
          progress: 0,
          wrongAttempts: 0,
          hintLevel: 0,
          revealed: false,
          traceSlips: 0,
          feedback: {
            kind: 'correct',
            name: current.name,
            typo: action.typo
          },
          feedbackId: state.feedbackId + 1,
          results: [...state.results, result],
          finished: nextIndex >= state.queue.length,
          angle: angleFor(state.stage, nextIndex)
        };
      }
    default:
      return state;
  }
}
export function useRecallController(card, initialStage) {
  const [state, dispatch] = useReducer(reducer, undefined, () => initial(initialStage, buildQueue(card, initialStage, null)));
  const current = state.queue[state.index];
  const completedIds = useMemo(() => {
    const keys = new Set(state.completed);
    return card.items.filter(entry => keys.has(entry.key)).flatMap(entry => entry.ids);
  }, [card.items, state.completed]);
  const start = useCallback((stage, store) => dispatch({
    type: 'start',
    stage,
    queue: buildQueue(card, stage, store)
  }), [card]);
  return {
    state,
    current,
    completedIds,
    start,
    setTyped: useCallback((typed, progress) => dispatch({
      type: 'type',
      typed,
      progress
    }), []),
    traceSlip: useCallback(() => dispatch({
      type: 'traceSlip'
    }), []),
    hint: useCallback(() => dispatch({
      type: 'hint'
    }), []),
    reveal: useCallback(() => dispatch({
      type: 'reveal'
    }), []),
    wrong: useCallback(close => dispatch({
      type: 'wrong',
      close
    }), []),
    correct: useCallback(typo => dispatch({
      type: 'correct',
      typo
    }), [])
  };
}
